'use strict';

var assert = require('./assertions');
var pako = require('pako');

function _interopNamespace(e) {
  if (e && e.__esModule) return e;
  var n = Object.create(null);
  if (e) {
    Object.keys(e).forEach(function (k) {
      if (k !== 'default') {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () { return e[k]; }
        });
      }
    });
  }
  n.default = e;
  return Object.freeze(n);
}

var assert__namespace = /*#__PURE__*/_interopNamespace(assert);

var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });
class Bitstring {
  static {
    __name(this, "Bitstring");
  }
  leftToRightIndexing;
  bits;
  length;
  /**
  * Creates a new Bitstring instance.
  *
  * @param {object} options - The options to use.
  * @param {Number} [options.length] - The length of the bitstring in bits;
  *   either this or `buffer` must be given, but not both.
  * @param {Uint8Array} [options.buffer] - The bitstring as a buffer of bytes
  *   where the bytes are always in big endian order (left most byte or most
  *   significant byte in the buffer will be the least significant and lowest
  *   index byte in the bitstring) and where the bit order
  *   is also little endian by default but may be specified as big endian
  *   by passing `leftToRightIndexing: false`; either this parameter or
  *   `length` must be given but not both.
  * @param {Boolean} [options.leftToRightIndexing=true] - Whether the bit
  *   order (NOT the byte order, which is *always* first byte in the buffer is
  *   the first byte-index into the bitstring, i.e., left to right) is left to
  *   right or not; the default is left to right, which matches what the byte
  *   order always is and, specifically, means that `0x80` or `0b1000000`
  *   means the first bit index is set in a given byte.
  * @param {Boolean} [options.littleEndianBits] - Deprecated; use
  *   `leftToRightIndexing` instead.
  */
  constructor({ length, buffer, leftToRightIndexing, littleEndianBits } = {}) {
    if (length && buffer) {
      throw new Error('Only one of "length" or "buffer" must be given.');
    }
    if (length !== void 0) {
      assert__namespace.isPositiveInteger(length, "length");
    } else {
      assert__namespace.isUint8Array(buffer, "buffer");
    }
    if (littleEndianBits !== void 0) {
      if (leftToRightIndexing !== void 0) {
        throw new Error('Using both "littleEndianBits" and "leftToRightIndexing" is not allowed.');
      }
      assert__namespace.isBoolean(littleEndianBits, "littleEndianBits");
      leftToRightIndexing = littleEndianBits;
    }
    if (leftToRightIndexing === void 0) {
      leftToRightIndexing = true;
    } else {
      assert__namespace.isBoolean(leftToRightIndexing, "leftToRightIndexing");
    }
    if (length) {
      this.bits = new Uint8Array(Math.ceil(Number(length) / 8));
      this.length = length;
    } else {
      this.bits = new Uint8Array(buffer.buffer);
      this.length = buffer.length * 8;
    }
    this.leftToRightIndexing = leftToRightIndexing;
  }
  set(position, on) {
    assert__namespace.isNumber(position, "position");
    assert__namespace.isBoolean(on, "on");
    const { length, leftToRightIndexing } = this;
    const { index, bit } = _parsePosition(position, length, leftToRightIndexing);
    if (on) {
      this.bits[index] |= bit;
    } else {
      this.bits[index] &= 255 ^ bit;
    }
  }
  get(position) {
    assert__namespace.isNumber(position, "position");
    const { length, leftToRightIndexing } = this;
    const { index, bit } = _parsePosition(position, length, leftToRightIndexing);
    return !!(this.bits[index] & bit);
  }
  async encodeBits() {
    const base64url = await import('base64url-universal');
    return base64url.encode(pako.gzip(this.bits));
  }
  static async decodeBits({ encoded }) {
    const base64url = await import('base64url-universal');
    assert__namespace.isString(encoded, "encoded");
    return pako.ungzip(base64url.decode(encoded));
  }
  async compressBits() {
    return pako.gzip(this.bits);
  }
  static async uncompressBits({ compressed }) {
    assert__namespace.isUint8Array(compressed, "compressed");
    return pako.ungzip(compressed);
  }
}
function _parsePosition(position, length, leftToRightIndexing) {
  assert__namespace.isNonNegativeInteger(position, "position");
  assert__namespace.isPositiveInteger(length, "length");
  assert__namespace.isBoolean(leftToRightIndexing, "leftToRightIndexing");
  if (position >= length) {
    throw new Error(`Position "${position}" is out of range "0-${Number(length) - 1}".`);
  }
  const index = Math.floor(Number(position) / 8);
  const rem = Number(position) % 8;
  const shift = leftToRightIndexing ? 7 - rem : rem;
  const bit = 1 << shift;
  return {
    index,
    bit
  };
}
__name(_parsePosition, "_parsePosition");

exports.Bitstring = Bitstring;
